import React, { useMemo, useState } from 'react';
import { Autocomplete, AutocompleteProps } from '@material-ui/lab';
import { useAsync, useDebounce } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { Box, LinearProgress, TextField } from '@material-ui/core';

import { Customer } from 'types/Customer';
import { getSelectedWarehouseID } from 'reducers/warehouses';
import {
  getCustomerSearchFilterOption,
  getCustomerSearchResultsSortingConfiguration,
} from 'reducers/configs/settings';
import { doCustomerSearch } from 'actions/searchCustomer';
import { handleSort } from 'containers/Col1/Search/Customers/util';
import { getUniqueCaptionForCustomerInList as getUniqueCaption } from 'components/inputs/CustomerInput/caption';

// TODO: Add a 'search' button to the end, like in backoffice
//   When clicked, open a MUI dialog containing a more advanced search
//   Where different filters can be set and which shows seveeral columns of data
const CustomerInput: React.FC<{
  value: Customer | number | null;
  setValue: (v: Customer | null) => void;
  extraParams?: any;
  modifyOptions?: (options: Customer[], text?: string) => Customer[];
  autoFocus?: boolean;
} & Partial<
  Omit<AutocompleteProps<number, false, undefined, false>, 'value' | 'onChange'>
>> = ({
  value,
  setValue,
  extraParams,
  modifyOptions = c => c,
  autoFocus,
  ...rest
}) => {
  const dispatch: ThunkDispatch<any, any, Action> = useDispatch();
  // Selectors
  const warehouseID = useSelector(getSelectedWarehouseID);
  const queryFilterOption = useSelector(getCustomerSearchFilterOption);
  const { enabled, criteria, direction } =
    useSelector(getCustomerSearchResultsSortingConfiguration) ?? {};

  const filterParameters = useMemo(() => {
    switch (queryFilterOption) {
      case 'byHomeStore':
        return { homeStoreID: warehouseID };
      case 'bySignupStore':
        return { signUpStoreID: warehouseID };
      default:
        return {};
    }
  }, [queryFilterOption, warehouseID]);

  const sortingParams = useMemo(() => {
    if (!enabled) {
      return {};
    }
    return {
      orderBy: criteria,
      orderByDir: direction,
    };
  }, [enabled, criteria, direction]);

  // State
  const [input, setInput] = useState('');
  const [searchText, setSearchText] = useState('');
  useDebounce(() => setSearchText(input), 400, [input]);
  const { value: customers, loading: isFetching, error: fetchError } = useAsync(
    () =>
      dispatch(
        doCustomerSearch({
          recordsOnPage: 100,
          getAddresses: 1,
          getBalanceInfo: 1,
          getBalanceWithoutPrepayments: 1,
          searchFromMiddle: 1,
          responseMode: 'detail',
          ...(Object.keys(sortingParams).length
            ? { searchName: searchText }
            : { searchNameIncrementally: searchText }),
          searchValue: searchText,
          mode: 'ALL',
          ...filterParameters,
          ...sortingParams,
          ...extraParams,
        }),
      ).then(res => dispatch(handleSort(res))),
    [searchText, filterParameters, extraParams, sortingParams],
  );

  const isWaitingForDebounce = input !== searchText;
  const loading = isFetching || isWaitingForDebounce;
  const loadingText = isWaitingForDebounce
    ? 'Waiting until typing finished'
    : `Searching for "${searchText}"`;
  const noOptionsText = fetchError
    ? `Failed to fetch: ${fetchError.message}`
    : `No results for "${searchText}"`;

  const [modifiedOptions, captions] = useMemo(() => {
    let modifiedOptions: Customer[];
    if (loading) {
      modifiedOptions = modifyOptions([], loadingText);
    } else if (!customers || customers.length === 0) {
      modifiedOptions = modifyOptions([], noOptionsText);
    } else {
      modifiedOptions = modifyOptions(customers);
    }
    const captions = modifiedOptions.map(getUniqueCaption);
    return [modifiedOptions, captions] as const;
  }, [loading, modifyOptions, loadingText, customers, noOptionsText]);

  return (
    <Autocomplete
      inputValue={input}
      onInputChange={(e, v) => setInput(v)}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      value={typeof value === 'number' ? value : value?.id}
      onChange={(e, v) =>
        setValue(modifiedOptions.find(o => o.id === v) ?? null)
      }
      options={modifiedOptions.map(o => o.id)}
      // Filtering already performed serverside, do not further hide
      // results clientside because they may be matching on properties
      // other than name
      filterOptions={options => options}
      renderInput={props => (
        <Box>
          <TextField variant="outlined" {...props} autoFocus={autoFocus} />
          {/* Visibility instead of conditional rendering to prevent layout shifts */}
          <Box visibility={loading ? 'visible' : 'hidden'}>
            <LinearProgress />
          </Box>
        </Box>
      )}
      getOptionLabel={(id: number) => {
        const index = modifiedOptions.findIndex(c => c.id === id);
        return captions[index];
      }}
      {...rest}
    />
  );
};
export default CustomerInput;
